import { FloatingField } from "design-system/components/FloatingField";
import { FloatingFieldSize } from "design-system/components/FloatingField/context";
import { LocationDetails } from "components/Form/Select/LocationSelect";
import { AutoFormField } from "models/autoForm";
import { FieldValues, UseFormReturn, useWatch } from "react-hook-form";
import { compact } from "lodash";

interface FieldByTypeProps extends AutoFormField {
  form: UseFormReturn<FieldValues>;
  size?: FloatingFieldSize;
  disabled?: boolean;
  onBlur?: () => void;
}

export default function FieldByType({
  name,
  label,
  type,
  form,
  required,
  options,
  optionsUrl,
  hiddenFields = [],
  associatedFields = [],
  isClearable,
  size,
  disabled,
  onBlur,
}: FieldByTypeProps) {
  const {
    control,
    setValue,
    formState: { errors },
  } = form;
  const errorMessage = errors[name]?.message as string | undefined;
  const [googleLocationId, rawLocation] = useWatch({ control, name: ["googleLocationId", "rawLocation"] });

  const handleLocationChange = (location: LocationDetails) => {
    setValue(name, compact([location.addressComponents.city, location.addressComponents.state]).join(", "), {
      shouldDirty: true,
    });

    [...hiddenFields, ...associatedFields].forEach((field) => {
      switch (field.name) {
        case "country":
          setValue(field.name, location.addressComponents.country, { shouldDirty: true });
          break;
        case "googleLocationId":
          setValue(field.name, location.id, { shouldDirty: true });
          break;
        case "rawLocation":
          setValue(field.name, location.label, { shouldDirty: true });
          break;
        case "timezone":
          setValue(field.name, location.timezone, { shouldDirty: true });
          break;
        default:
          break;
      }
    });
  };

  const locationInitialValue = googleLocationId
    ? { locationId: googleLocationId as string, label: rawLocation as string }
    : undefined;

  return (
    <FloatingField name={name} size={size} errors={errorMessage} required={required}>
      <FloatingField.Label>{label}</FloatingField.Label>
      {type === "text" && <FloatingField.Text control={control} name={name} disabled={disabled} onBlur={onBlur} />}
      {type === "email" && (
        <FloatingField.Text control={control} name={name} type="email" disabled={disabled} onBlur={onBlur} />
      )}
      {type === "date" && (
        <FloatingField.Calendar control={control} name={name} isClearable disabled={disabled} onBlur={onBlur} />
      )}
      {type === "timezoneSelect" && (
        <FloatingField.Timezone control={control} name={name} disabled={disabled} onBlur={onBlur} />
      )}
      {type === "singleSelect" && (
        <FloatingField.StaticSelect
          control={control}
          name={name}
          options={options || []}
          isDisabled={disabled}
          onBlur={onBlur}
        />
      )}
      {type === "createableSingleSelect" && (
        <FloatingField.CreatableSingle
          control={control}
          name={name}
          resourceUrl={optionsUrl as string}
          isClearable={isClearable}
          isDisabled={disabled}
          onBlur={onBlur}
        />
      )}
      {type === "userSelect" && (
        <FloatingField.UserSelect control={control} name={name} isDisabled={disabled} onBlur={onBlur} />
      )}
      {type === "locationSelect" && (
        <FloatingField.Location initialValue={locationInitialValue} onChange={handleLocationChange} />
      )}
    </FloatingField>
  );
}
